'use client';

import React, { useState, useRef, useCallback } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { getChatCompletion } from '@/lib/ai/chatCompletion';
import { createClient } from '@/lib/supabase/client';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface AsbabQuestChallenge {
  title: string;
  description: string;
  divine_name: string;
  action_type: 'observation' | 'service' | 'reflection' | 'community' | 'ecology';
  difficulty: 'accessible' | 'moderate' | 'deep';
  location_type: 'indoor' | 'outdoor' | 'community' | 'any';
}

interface NameResonance {
  name: string;
  arabic: string;
  transliteration: string;
  meaning: string;
  evidence: string;
  reflection_prompt: string;
  ayat_ref: string;
  ayat_text: string;
  confidence: 'primary' | 'secondary' | 'tertiary';
  asbab_quests: AsbabQuestChallenge[];
}

interface AnalysisResult {
  scene_description: string;
  detected_elements: string[];
  names_resonances: NameResonance[];
  overall_reflection: string;
  amanah_prompt: string;
  quest_suggestion: string;
  auto_tags: string[];
  biome_detected: string;
  species_detected: string[];
}

interface SubmissionState {
  status: 'idle' | 'submitting' | 'success' | 'error';
  message: string;
}

// ─── ASBAB QUEST CHALLENGES BY DIVINE NAME ────────────────────────────────────

const ASBAB_QUEST_BY_NAME: Record<string, AsbabQuestChallenge[]> = {
  'Al-Khaliq': [
    { title: 'Document a Creation Sequence', description: 'Photograph 3 stages of a natural process (seed, sprout, plant) and reflect on Al-Khaliq in each stage.', divine_name: 'Al-Khaliq', action_type: 'observation', difficulty: 'accessible', location_type: 'outdoor' },
    { title: 'Creation Mapping Walk', description: 'Walk your neighborhood and identify 5 created things whose origin you can trace back to Al-Khaliq through their materials.', divine_name: 'Al-Khaliq', action_type: 'reflection', difficulty: 'moderate', location_type: 'outdoor' },
  ],
  'Ar-Razzaq': [
    { title: 'Provision Audit', description: 'List 10 provisions you received today and trace each one back to its ultimate source — Al-Razzaq.', divine_name: 'Ar-Razzaq', action_type: 'reflection', difficulty: 'accessible', location_type: 'any' },
    { title: 'Community Food Mapping', description: 'Map 3 food access points in your ward and connect with a local food security organization (CIOGC, Zakat Foundation).', divine_name: 'Ar-Razzaq', action_type: 'community', difficulty: 'moderate', location_type: 'community' },
  ],
  'Al-Hayy': [
    { title: 'Living Systems Observation', description: 'Spend 20 minutes observing a living ecosystem (park, garden, waterway) and document 5 signs of Al-Hayy.', divine_name: 'Al-Hayy', action_type: 'observation', difficulty: 'accessible', location_type: 'outdoor' },
    { title: 'Watershed Caretaking', description: 'Join a local watershed cleanup or tree-planting event through MANA or MAS environmental programs.', divine_name: 'Al-Hayy', action_type: 'ecology', difficulty: 'moderate', location_type: 'outdoor' },
  ],
  'Al-Nur': [
    { title: 'Light Observation Journal', description: 'Document how light changes in your environment over one hour — shadows, reflections, color shifts — as signs of Al-Nur.', divine_name: 'Al-Nur', action_type: 'observation', difficulty: 'accessible', location_type: 'any' },
    { title: 'Community Illumination', description: 'Identify a dark corner of your community (literal or metaphorical) and take one step toward bringing light to it.', divine_name: 'Al-Nur', action_type: 'community', difficulty: 'deep', location_type: 'community' },
  ],
  'Al-Latif': [
    { title: 'Subtlety Walk', description: 'Walk slowly through a natural space and document 5 subtle details you would normally miss — evidence of Al-Latif.', divine_name: 'Al-Latif', action_type: 'observation', difficulty: 'accessible', location_type: 'outdoor' },
    { title: 'Gentle Service', description: 'Perform one act of quiet, unannounced service in your community today — embodying Al-Latif.', divine_name: 'Al-Latif', action_type: 'service', difficulty: 'accessible', location_type: 'community' },
  ],
  'Al-Musawwir': [
    { title: 'Form Documentation', description: 'Photograph 7 different natural forms (leaf shapes, cloud formations, rock patterns) and reflect on Al-Musawwir as the Fashioner.', divine_name: 'Al-Musawwir', action_type: 'observation', difficulty: 'accessible', location_type: 'outdoor' },
  ],
  'Al-Qadir': [
    { title: 'Power Mapping', description: 'Identify 3 natural forces in your environment (wind, water, growth) and document how they reflect Al-Qadir.', divine_name: 'Al-Qadir', action_type: 'observation', difficulty: 'accessible', location_type: 'outdoor' },
    { title: 'Empowerment Quest', description: 'Connect with ICNA or MAS to find a community empowerment program and participate or volunteer.', divine_name: 'Al-Qadir', action_type: 'community', difficulty: 'moderate', location_type: 'community' },
  ],
  'Al-Haqq': [
    { title: 'Reality Check', description: 'Identify one injustice in your community and research one organization (MANA Justice, CIOGC) working to address it.', divine_name: 'Al-Haqq', action_type: 'community', difficulty: 'moderate', location_type: 'any' },
    { title: 'Truth Documentation', description: 'Document one overlooked truth about your local ecosystem — a species, a water source, a soil condition.', divine_name: 'Al-Haqq', action_type: 'observation', difficulty: 'accessible', location_type: 'outdoor' },
  ],
  'Al-Bari': [
    { title: 'Evolution Observation', description: 'Document a natural growth process over 3 days — a plant, a cloud formation, a body of water — as evidence of Al-Bari.', divine_name: 'Al-Bari', action_type: 'observation', difficulty: 'moderate', location_type: 'outdoor' },
  ],
  'Al-Muhyi': [
    { title: 'Restoration Quest', description: 'Participate in a community garden, tree planting, or soil restoration project through a local Islamic organization.', divine_name: 'Al-Muhyi', action_type: 'ecology', difficulty: 'moderate', location_type: 'outdoor' },
  ],
};

// ─── ANALYSIS FUNCTION ────────────────────────────────────────────────────────

async function analyzeImageForDivineNames(base64Image: string, mimeType: string): Promise<AnalysisResult> {
  const systemPrompt = `You are an Islamic scholar and naturalist helping Muslims recognize the Names and Attributes of Allah (Asma ul-Husna) in creation. When analyzing images of nature, creation, or community scenes, you identify which Divine Names are most evidently reflected in what is depicted.

Your analysis should be:
- Grounded in classical Islamic scholarship on the Names of Allah
- Connected to specific Quranic ayat (verses) with accurate references
- Practical and reflective — helping the viewer deepen their taqwa (God-consciousness)
- Focused on what is actually visible in the image
- Include suggested Asbab Quest challenges that connect the detected Names to caretaking actions

Return ONLY valid JSON, no markdown.`;

  const userPrompt = `Analyze this image and identify which Names and Attributes of Allah (Asma ul-Husna) are most evidently reflected in what you see. 

Return a JSON object with this exact structure:
{
  "scene_description": "Brief description of what is depicted in the image",
  "detected_elements": ["element1", "element2", "element3"],
  "biome_detected": "urban_forest|watershed|community_garden|urban_sky|desert|coastal|grassland|other",
  "species_detected": ["species or element name"],
  "auto_tags": ["tag1", "tag2"],
  "names_resonances": [
    {
      "name": "Al-Khaliq",
      "arabic": "الْخَالِق",
      "transliteration": "Al-Khaliq",
      "meaning": "The Creator",
      "evidence": "How this Name is evidenced in the image",
      "reflection_prompt": "A contemplative question for the viewer",
      "ayat_ref": "Surah:Ayah",
      "ayat_text": "Relevant Quranic verse text",
      "confidence": "primary",
      "asbab_quests": [
        {
          "title": "Quest title",
          "description": "Specific caretaking action inspired by this Name and this image",
          "divine_name": "Al-Khaliq",
          "action_type": "observation",
          "difficulty": "accessible",
          "location_type": "outdoor"
        }
      ]
    }
  ],
  "overall_reflection": "A paragraph connecting the image to the Names of Allah and inviting deeper reflection",
  "amanah_prompt": "What trust/responsibility does this image reveal for the viewer as a caretaker?",
  "quest_suggestion": "A suggested Asbab Quest action inspired by this image"
}

Include 3-5 Names of Allah with confidence levels: primary (most evident), secondary, or tertiary.
For each Name, suggest 1-2 specific Asbab Quest challenges that connect that Name to real caretaking actions.`;

  const response = await getChatCompletion(
    'ANTHROPIC',
    'claude-sonnet-4-5-20250929',
    [{
      role: 'user',
      content: [
        { type: 'text', text: userPrompt },
        { type: 'image_url', image_url: { url: `data:${mimeType};base64,${base64Image}` } }
      ]
    }],
    { temperature: 0.6, max_tokens: 3000 }
  );

  const content = response?.choices?.[0]?.message?.content || '';
  const jsonMatch = content.match(/\{[\s\S]*\}/);
  if (!jsonMatch) throw new Error('No valid JSON in response');
  const parsed = JSON.parse(jsonMatch[0]);

  // Enrich with pre-defined Asbab Quest challenges for each detected Name
  if (parsed.names_resonances) {
    parsed.names_resonances = parsed.names_resonances.map((nr: NameResonance) => {
      const predefined = ASBAB_QUEST_BY_NAME[nr.name] || [];
      const aiQuests = nr.asbab_quests || [];
      return {
        ...nr,
        asbab_quests: [...aiQuests, ...predefined].slice(0, 3),
      };
    });
  }

  return parsed;
}

// ─── CONFIDENCE COLORS ────────────────────────────────────────────────────────

const CONFIDENCE_CONFIG = {
  primary:   { color: '#c9a84c', label: 'Primary Resonance',   bg: 'rgba(201,168,76,0.08)',   border: 'rgba(201,168,76,0.3)' },
  secondary: { color: '#2d8a87', label: 'Secondary Resonance', bg: 'rgba(45,138,135,0.08)',   border: 'rgba(45,138,135,0.3)' },
  tertiary:  { color: '#8a8ab8', label: 'Tertiary Resonance',  bg: 'rgba(138,138,184,0.08)',  border: 'rgba(138,138,184,0.3)' },
};

const ACTION_COLORS: Record<string, string> = {
  observation: '#2d8a87',
  service:     '#2d7a47',
  reflection:  '#c9a84c',
  community:   '#8a8ab8',
  ecology:     '#4a9a5a',
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function CreationLensPage() {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string>('');
  const [imageMime, setImageMime] = useState<string>('image/jpeg');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string>('');
  const [activeNameIdx, setActiveNameIdx] = useState<number>(0);
  const [activeQuestIdx, setActiveQuestIdx] = useState<number>(0);
  const [submission, setSubmission] = useState<SubmissionState>({ status: 'idle', message: '' });
  const [reflectionText, setReflectionText] = useState('');
  const [visibilitySetting, setVisibilitySetting] = useState<'private' | 'community' | 'public'>('community');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const supabase = createClient();

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) { setError('Please upload a JPEG, PNG, GIF, or WebP image.'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('Image must be under 10MB.'); return; }
    setError(''); setResult(null); setImageMime(file.type);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      setImagePreview(dataUrl);
      setImageBase64(dataUrl.split(',')[1]);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!imageBase64) return;
    setAnalyzing(true); setError(''); setResult(null);
    try {
      const analysis = await analyzeImageForDivineNames(imageBase64, imageMime);
      setResult(analysis);
      setActiveNameIdx(0);
      setActiveQuestIdx(0);
    } catch {
      setError('Analysis failed. Please try again with a different image.');
    } finally {
      setAnalyzing(false);
    }
  }, [imageBase64, imageMime]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const fakeEvent = { target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileChange(fakeEvent);
    }
  }, [handleFileChange]);

  // ── Submit to Supabase ecosystem_observations ─────────────────────────────
  const handleSubmitObservation = useCallback(async () => {
    if (!result || !imageBase64) return;
    setSubmission({ status: 'submitting', message: '' });

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const activeNameData = result.names_resonances[activeNameIdx];

      // Build auto-tag array from detected elements + Names
      const autoTags = [
        ...(result.auto_tags || []),
        ...(result.detected_elements || []).slice(0, 5),
        ...(result.names_resonances || []).map(n => n.name),
        result.biome_detected || 'unknown',
      ].filter(Boolean).slice(0, 20);

      // Insert into ecosystem_observations
      const { error: insertError } = await supabase
        .from('ecosystem_observations')
        .insert({
          observer_id: user?.id || null,
          photo_url: imagePreview || '',
          reflection_text: reflectionText || result.overall_reflection,
          biome: result.biome_detected || 'other',
          location_name: 'Submitted via Creation Lens',
          species_identified: result.species_detected || [],
          quranic_citation: activeNameData?.ayat_ref || '',
          gallery_visibility: visibilitySetting,
          status: 'pending',
          // Store Names and auto-tags as JSON in notes field if available
          notes: JSON.stringify({
            auto_tags: autoTags,
            names_resonances: result.names_resonances?.map(n => ({ name: n.name, arabic: n.arabic, confidence: n.confidence })),
            amanah_prompt: result.amanah_prompt,
            quest_suggestion: result.quest_suggestion,
            scene_description: result.scene_description,
          }),
        });

      if (insertError) throw insertError;

      setSubmission({ status: 'success', message: 'Observation submitted for community guardian review. JazakAllahu Khayran.' });
    } catch (err) {
      setSubmission({ status: 'error', message: 'Submission failed. Please try again.' });
    }
  }, [result, imageBase64, imagePreview, activeNameIdx, reflectionText, visibilitySetting, supabase]);

  const activeNameData = result?.names_resonances?.[activeNameIdx];
  const activeQuests = activeNameData?.asbab_quests || [];

  return (
    <div className="min-h-screen" style={{ background: '#060d12' }}>
      <Header />

      {/* Hero */}
      <section style={{ background: '#060d12', borderBottom: '1px solid rgba(201,168,76,0.1)', padding: '64px 24px 48px' }}>
        <div className="max-w-3xl mx-auto text-center">
          <p style={{ fontFamily: 'monospace', fontSize: 10, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 12 }}>
            عَدَسَة الْحَقِيقَة الدَّاخِلِيَّة · INNER REALITY LENS
          </p>
          <h1 style={{ fontFamily: 'monospace', fontSize: 'clamp(18px, 4vw, 32px)', color: '#c9a84c', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 16 }}>
            CREATION LENS
          </h1>
          <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: 'rgba(212,197,169,0.8)', lineHeight: 1.8, marginBottom: 12 }}>
            "Soon We will show them Our Ayat in the horizons and in themselves until it becomes clear to them that it is the Truth."
          </p>
          <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.4)', letterSpacing: '.12em' }}>
            QURAN 41:53
          </p>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* ── Upload Panel ─────────────────────────────────────────────── */}
          <div>
            <div
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => !imagePreview && fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${imagePreview ? 'rgba(201,168,76,0.3)' : 'rgba(201,168,76,0.15)'}`,
                borderRadius: 12,
                minHeight: 280,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: imagePreview ? 'default' : 'pointer',
                background: 'rgba(201,168,76,0.02)',
                overflow: 'hidden',
                position: 'relative',
                transition: 'border-color 0.2s',
              }}
            >
              {imagePreview ? (
                <>
                  <img src={imagePreview} alt="Creation to analyze" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(6,13,18,0.8) 0%, transparent 50%)' }} />
                  <button
                    onClick={e => { e.stopPropagation(); setImagePreview(null); setImageBase64(''); setResult(null); setSubmission({ status: 'idle', message: '' }); }}
                    style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', color: 'rgba(212,197,169,0.7)', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em' }}
                  >
                    ✕ Clear
                  </button>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: 32 }}>
                  <div style={{ fontSize: 40, marginBottom: 16, opacity: 0.4 }}>◎</div>
                  <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(201,168,76,0.5)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
                    Upload a Creation Photo
                  </p>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(212,197,169,0.4)', lineHeight: 1.6 }}>
                    Photograph nature, your environment, or any creation to detect Divine Names and Attributes
                  </p>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.3)', marginTop: 12 }}>
                    JPEG · PNG · WebP · Max 10MB
                  </p>
                </div>
              )}
            </div>

            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileChange} style={{ display: 'none' }} />

            {imagePreview && (
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{ width: '100%', marginTop: 8, background: 'transparent', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 6, padding: '8px', cursor: 'pointer', fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '.08em' }}
              >
                ↑ Change Image
              </button>
            )}

            {error && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(180,60,60,0.1)', border: '1px solid rgba(180,60,60,0.3)', borderRadius: 6 }}>
                <p style={{ fontFamily: 'monospace', fontSize: 10, color: '#c06060' }}>{error}</p>
              </div>
            )}

            <button
              onClick={handleAnalyze}
              disabled={!imageBase64 || analyzing}
              style={{
                width: '100%', marginTop: 16,
                background: imageBase64 && !analyzing ? 'rgba(201,168,76,0.12)' : 'rgba(201,168,76,0.04)',
                border: `1px solid ${imageBase64 && !analyzing ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.12)'}`,
                borderRadius: 8, padding: '14px',
                cursor: imageBase64 && !analyzing ? 'pointer' : 'not-allowed',
                fontFamily: 'monospace', fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase',
                color: imageBase64 && !analyzing ? '#c9a84c' : 'rgba(201,168,76,0.3)',
                transition: 'all 0.2s',
              }}
            >
              {analyzing ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>◎</span>
                  Detecting Divine Names...
                </span>
              ) : '✦ Detect Divine Names'}
            </button>

            {/* Submission panel (shown after analysis) */}
            {result && (
              <div style={{ marginTop: 20, background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 10, padding: 16 }}>
                <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.7)', marginBottom: 10 }}>
                  ⊕ Submit to Community Observations
                </p>
                <textarea
                  value={reflectionText}
                  onChange={e => setReflectionText(e.target.value)}
                  placeholder="Add your personal reflection (optional — AI reflection will be used if left blank)..."
                  rows={3}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 11, fontFamily: 'inherit', resize: 'vertical', outline: 'none', marginBottom: 10 }}
                />
                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                  {(['private', 'community', 'public'] as const).map(v => (
                    <button
                      key={v}
                      onClick={() => setVisibilitySetting(v)}
                      style={{
                        flex: 1, padding: '6px 4px', borderRadius: 5, cursor: 'pointer', fontFamily: 'monospace', fontSize: 8, letterSpacing: '.06em', textTransform: 'capitalize',
                        background: visibilitySetting === v ? 'rgba(45,122,71,0.15)' : 'transparent',
                        border: `1px solid ${visibilitySetting === v ? 'rgba(45,122,71,0.4)' : 'rgba(255,255,255,0.08)'}`,
                        color: visibilitySetting === v ? '#2d7a47' : 'rgba(212,197,169,0.4)',
                      }}
                    >
                      {v}
                    </button>
                  ))}
                </div>
                <button
                  onClick={handleSubmitObservation}
                  disabled={submission.status === 'submitting' || submission.status === 'success'}
                  style={{
                    width: '100%', padding: '10px', borderRadius: 6, cursor: submission.status === 'submitting' ? 'not-allowed' : 'pointer',
                    background: submission.status === 'success' ? 'rgba(45,122,71,0.2)' : 'rgba(45,122,71,0.1)',
                    border: `1px solid ${submission.status === 'success' ? 'rgba(45,122,71,0.5)' : 'rgba(45,122,71,0.3)'}`,
                    color: '#2d7a47', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                  }}
                >
                  {submission.status === 'submitting' ? 'Submitting...' : submission.status === 'success' ? '✓ Submitted' : '⊕ Submit Observation'}
                </button>
                {submission.message && (
                  <p style={{ fontFamily: 'monospace', fontSize: 9, color: submission.status === 'success' ? '#2d7a47' : '#c06060', marginTop: 8, lineHeight: 1.5 }}>
                    {submission.message}
                  </p>
                )}
                {/* Auto-tags preview */}
                {result.auto_tags && result.auto_tags.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Auto-Tags</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {result.auto_tags.slice(0, 8).map((tag, i) => (
                        <span key={i} style={{ fontFamily: 'monospace', fontSize: 8, padding: '2px 8px', borderRadius: 20, background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: 'rgba(201,168,76,0.6)' }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Results Panel ─────────────────────────────────────────────── */}
          <div>
            {!result && !analyzing && (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 32, textAlign: 'center' }}>
                <div style={{ fontSize: 48, opacity: 0.15, marginBottom: 20 }}>✦</div>
                <p style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: 'rgba(212,197,169,0.4)', lineHeight: 1.8 }}>
                  Upload a photo of creation to detect the Names and Attributes of Allah reflected within it
                </p>
              </div>
            )}

            {analyzing && (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 32, textAlign: 'center' }}>
                <div style={{ fontSize: 48, opacity: 0.4, marginBottom: 20, animation: 'pulse 2s ease-in-out infinite' }}>◎</div>
                <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(201,168,76,0.6)', letterSpacing: '.12em', textTransform: 'uppercase' }}>
                  Detecting Divine Names...
                </p>
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(212,197,169,0.4)', marginTop: 12, lineHeight: 1.6 }}>
                  Analyzing creation for Ayat and resonances with the Names of Allah
                </p>
              </div>
            )}

            {result && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Scene description */}
                <div style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 8, padding: 14 }}>
                  <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 6 }}>
                    Scene Detected
                  </p>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(212,197,169,0.8)', lineHeight: 1.7 }}>
                    {result.scene_description}
                  </p>
                  {result.biome_detected && (
                    <span style={{ display: 'inline-block', marginTop: 8, fontFamily: 'monospace', fontSize: 8, padding: '2px 10px', borderRadius: 20, background: 'rgba(45,138,135,0.1)', border: '1px solid rgba(45,138,135,0.3)', color: '#2d8a87' }}>
                      {result.biome_detected.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>

                {/* Names tabs */}
                {result.names_resonances && result.names_resonances.length > 0 && (
                  <div>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 8 }}>
                      Divine Names Detected
                    </p>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                      {result.names_resonances.map((nr, idx) => {
                        const cfg = CONFIDENCE_CONFIG[nr.confidence] || CONFIDENCE_CONFIG.tertiary;
                        return (
                          <button
                            key={idx}
                            onClick={() => { setActiveNameIdx(idx); setActiveQuestIdx(0); }}
                            style={{
                              padding: '6px 12px', borderRadius: 20, cursor: 'pointer', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.06em',
                              background: activeNameIdx === idx ? cfg.bg : 'transparent',
                              border: `1px solid ${activeNameIdx === idx ? cfg.border : 'rgba(201,168,76,0.12)'}`,
                              color: activeNameIdx === idx ? cfg.color : 'rgba(212,197,169,0.4)',
                              transition: 'all 0.15s',
                            }}
                          >
                            {nr.name}
                          </button>
                        );
                      })}
                    </div>

                    {/* Active name detail */}
                    {activeNameData && (() => {
                      const cfg = CONFIDENCE_CONFIG[activeNameData.confidence] || CONFIDENCE_CONFIG.tertiary;
                      return (
                        <div style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 10, padding: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                            <div>
                              <h3 style={{ fontFamily: 'monospace', fontSize: 14, color: cfg.color, letterSpacing: '.08em', marginBottom: 2 }}>
                                {activeNameData.name}
                              </h3>
                              <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: cfg.color, direction: 'rtl', marginBottom: 2 }}>
                                {activeNameData.arabic}
                              </p>
                              <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.6)', fontStyle: 'italic' }}>
                                {activeNameData.meaning}
                              </p>
                            </div>
                            <span style={{ fontFamily: 'monospace', fontSize: 7, padding: '3px 8px', borderRadius: 20, background: `${cfg.color}18`, border: `1px solid ${cfg.color}44`, color: cfg.color, letterSpacing: '.08em', textTransform: 'uppercase', flexShrink: 0 }}>
                              {cfg.label}
                            </span>
                          </div>

                          <div style={{ marginBottom: 10, padding: '8px 12px', background: 'rgba(6,13,18,0.4)', borderRadius: 6 }}>
                            <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Evidence in Image</p>
                            <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.75)', lineHeight: 1.7 }}>{activeNameData.evidence}</p>
                          </div>

                          <div style={{ marginBottom: 10, padding: '8px 12px', background: 'rgba(6,13,18,0.4)', borderRadius: 6 }}>
                            <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Reflection Prompt</p>
                            <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.75)', lineHeight: 1.7, fontStyle: 'italic' }}>"{activeNameData.reflection_prompt}"</p>
                          </div>

                          {activeNameData.ayat_text && (
                            <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 6 }}>
                              <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(201,168,76,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>
                                Quranic Reference · {activeNameData.ayat_ref}
                              </p>
                              <p style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: 'rgba(212,197,169,0.7)', lineHeight: 1.8, fontStyle: 'italic' }}>
                                "{activeNameData.ayat_text}"
                              </p>
                            </div>
                          )}

                          {/* Asbab Quest Challenges */}
                          {activeQuests.length > 0 && (
                            <div>
                              <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.7)', marginBottom: 8 }}>
                                ⬡ Asbab Quest Challenges
                              </p>
                              <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                                {activeQuests.map((_, qi) => (
                                  <button
                                    key={qi}
                                    onClick={() => setActiveQuestIdx(qi)}
                                    style={{
                                      padding: '4px 10px', borderRadius: 20, cursor: 'pointer', fontFamily: 'monospace', fontSize: 8,
                                      background: activeQuestIdx === qi ? 'rgba(45,122,71,0.15)' : 'transparent',
                                      border: `1px solid ${activeQuestIdx === qi ? 'rgba(45,122,71,0.4)' : 'rgba(45,122,71,0.15)'}`,
                                      color: activeQuestIdx === qi ? '#2d7a47' : 'rgba(45,122,71,0.5)',
                                    }}
                                  >
                                    Quest {qi + 1}
                                  </button>
                                ))}
                              </div>
                              {activeQuests[activeQuestIdx] && (
                                <div style={{ background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 8, padding: 12 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                    <span style={{
                                      fontFamily: 'monospace', fontSize: 7, padding: '2px 8px', borderRadius: 20,
                                      background: `${ACTION_COLORS[activeQuests[activeQuestIdx].action_type] || '#2d7a47'}18`,
                                      border: `1px solid ${ACTION_COLORS[activeQuests[activeQuestIdx].action_type] || '#2d7a47'}44`,
                                      color: ACTION_COLORS[activeQuests[activeQuestIdx].action_type] || '#2d7a47',
                                      letterSpacing: '.08em', textTransform: 'uppercase',
                                    }}>
                                      {activeQuests[activeQuestIdx].action_type}
                                    </span>
                                    <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', letterSpacing: '.06em' }}>
                                      {activeQuests[activeQuestIdx].difficulty} · {activeQuests[activeQuestIdx].location_type}
                                    </span>
                                  </div>
                                  <p style={{ fontFamily: 'monospace', fontSize: 10, color: '#2d7a47', letterSpacing: '.04em', marginBottom: 6 }}>
                                    {activeQuests[activeQuestIdx].title}
                                  </p>
                                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,197,169,0.7)', lineHeight: 1.7 }}>
                                    {activeQuests[activeQuestIdx].description}
                                  </p>
                                  <Link href="/asbab-quest" style={{ display: 'inline-block', marginTop: 10, fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: '#2d7a47', textDecoration: 'none', padding: '5px 12px', border: '1px solid rgba(45,122,71,0.3)', borderRadius: 4 }}>
                                    → View All Asbab Quests
                                  </Link>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Overall reflection */}
                {result.overall_reflection && (
                  <div style={{ background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 8, padding: 14 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 8 }}>
                      Overall Reflection
                    </p>
                    <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(212,197,169,0.75)', lineHeight: 1.8 }}>
                      {result.overall_reflection}
                    </p>
                  </div>
                )}

                {/* Amanah prompt */}
                {result.amanah_prompt && (
                  <div style={{ background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 8, padding: 14 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(45,138,135,0.6)', marginBottom: 6 }}>
                      Amanah — Your Caretaking Trust
                    </p>
                    <p style={{ fontFamily: "'Amiri', serif", fontSize: 15, color: 'rgba(212,197,169,0.8)', lineHeight: 1.8, fontStyle: 'italic' }}>
                      "{result.amanah_prompt}"
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </div>
  );
}
